import React, { useEffect, useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  AlertCircle, 
  HelpCircle,
  Construction,
  Save,
  RotateCw,
  Plus,
  Trash2
} from 'lucide-react';
import { getDevTasks, saveDevTasks, type DevTasksCollection, type DevTaskData } from '../lib/api';

const PrioIcon = ({ type }: { type: DevTaskData['prio'] }) => {
  switch (type) {
    case 'high': return <span className="text-red-500" title="Alta Prioridad">🔴</span>;
    case 'medium': return <span className="text-yellow-500" title="Prioridad Media">🟡</span>;
    case 'low': return <span className="text-emerald-500" title="Baja Prioridad">🟢</span>;
    default: return null;
  }
};

const EditableCell = ({ 
  value, 
  onChange, 
  className = "" 
}: { 
  value: string; 
  onChange: (val: string) => void; 
  className?: string 
}) => {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-transparent border-none focus:ring-1 focus:ring-white/20 rounded px-1 w-full text-white ${className}`}
    />
  );
};

export function DevTasks() {
  const [tasksMap, setTasksMap] = useState<DevTasksCollection>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getDevTasks();
      setTasksMap(data);
      setDirty(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveDevTasks(tasksMap);
      setDirty(false);
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateTask = (id: string, field: keyof DevTaskData, val: any) => {
    setTasksMap(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: val
      }
    }));
    setDirty(true);
  };

  const updateTaskId = (oldId: string, newId: string) => {
    if (oldId === newId) return;
    if (tasksMap[newId]) {
      alert('El ID ya existe');
      return;
    }
    const newMap = { ...tasksMap };
    newMap[newId] = newMap[oldId];
    delete newMap[oldId];
    setTasksMap(newMap);
    setDirty(true);
  };

  const addTask = (category: string) => {
    const newId = `NEW-${Date.now().toString().slice(-4)}`;
    const newTask: DevTaskData = {
      category,
      prio: 'medium',
      status: 'pending',
      assigned: 'Usuario',
      detail: 'Nueva Tarea',
      desc: 'Descripción...',
      comments: ''
    };
    setTasksMap(prev => ({
      ...prev,
      [newId]: newTask
    }));
    setDirty(true);
  };
  
  const deleteTask = (id: string) => {
      if (!confirm('¿Eliminar tarea?')) return;
      const newMap = { ...tasksMap };
      delete newMap[id];
      setTasksMap(newMap);
      setDirty(true);
  };

  // Agrupar por categoría
  const groupedTasks = useMemo(() => {
    const groups: Record<string, { id: string; data: DevTaskData }[]> = {};
    Object.entries(tasksMap).forEach(([id, data]) => {
      const cat = data.category || 'Sin Categoría';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ id, data });
    });
    // Ordenar claves de categorías para consistencia
    return Object.keys(groups).sort().map(key => ({
        title: key,
        tasks: groups[key].sort((a,b) => a.id.localeCompare(b.id)) 
    }));
  }, [tasksMap]);

  if (loading && Object.keys(tasksMap).length === 0) {
    return <div className="p-10 text-center text-white/50">Cargando tareas...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">🚀 Dashboard de Desarrollo</h1>
          <p className="text-white/60">
            Seguimiento de tareas (Sincronizado via JSON Key-Value Store)
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={load} 
            className="o-btn-ghost p-2" 
            title="Recargar"
          >
            <RotateCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          {dirty && (
            <button
              onClick={save}
              disabled={saving}
              className="hidden md:flex px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium items-center gap-2 animate-pulse"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          )}
        </div>
      </header>
      
       {/* Botón flotante para guardar cambios siempre visible */}
       {dirty && (
        <div className="fixed bottom-8 right-8 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <button
            onClick={save}
            disabled={saving}
            className="shadow-2xl px-6 py-4 bg-blue-600 hover:bg-blue-500 rounded-full text-white font-bold flex items-center gap-3 transition-transform hover:scale-105"
          >
            <Save className="w-6 h-6" />
            <span className="text-lg">{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">Detectados cambios</span>
          </button>
        </div>
      )}
      
       {/* Botón flotante o panel para crear categoría nueva? Provisorio: Botón arriba */}
       <div className="flex gap-2">
            <button 
                onClick={() => addTask('General')}
                className="text-xs bg-white/10 hover:bg-white/20 px-3 py-2 rounded text-white flex items-center gap-2"
            >
                <Plus size={14} /> Nueva Tarea (General)
            </button>
       </div>

      <div className="space-y-8">
        {groupedTasks.map((section) => (
          <section key={section.title} className="o-card p-5 border border-white/10 bg-white/5">
            <div className="mb-4 pb-4 border-b border-white/10 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
              </div>
              <button 
                onClick={() => addTask(section.title)}
                className="text-xs flex items-center gap-1 bg-white/5 hover:bg-white/10 px-2 py-1 rounded text-white/70 transition-colors"
                title="Añadir tarea a esta sección"
              >
                <Plus size={14} /> Nueva Tarea
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-white/40 border-b border-white/5">
                    <th className="py-2 px-3 w-32 text-center">Ticket ID</th>
                    <th className="py-2 px-3 w-24 text-center">Prio</th>
                    <th className="py-2 px-3 w-28 text-center">Estado</th>
                    <th className="py-2 px-3 w-32">Asignado</th>
                    <th className="py-2 px-3">Tarea / Descripción</th>
                    <th className="py-2 px-3 w-1/4">Comentarios</th>
                    <th className="py-2 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {section.tasks.map(({ id, data }) => (
                    <tr key={id} className="hover:bg-white/5 transition-colors group">
                      <td className="py-3 px-3 font-mono text-white/50 text-xs text-center">
                        <EditableCell 
                          value={id} 
                          onChange={(v) => updateTaskId(id, v)}
                          className="text-center font-mono opacity-60 focus:opacity-100 font-bold"
                        />
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="relative inline-block w-full">
                          <select 
                            value={data.prio}
                            onChange={(e) => updateTask(id, 'prio', e.target.value)}
                            className="bg-transparent appearance-none text-center border border-white/10 hover:border-white/30 rounded p-1 text-xs focus:bg-gray-800 focus:outline-none w-full cursor-pointer"
                          >
                            <option value="high">🔴 Alta</option>
                            <option value="medium">🟡 Media</option>
                            <option value="low">🟢 Baja</option>
                          </select>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                         <div className="relative inline-block w-full">
                          <select 
                            value={data.status}
                            onChange={(e) => updateTask(id, 'status', e.target.value)}
                            className="bg-transparent appearance-none text-center border border-white/10 hover:border-white/30 rounded p-1 text-xs focus:bg-gray-800 focus:outline-none w-full cursor-pointer"
                          >
                            <option value="pending">⬜ Pendiente</option>
                            <option value="in_progress">🚧 Progreso</option>
                            <option value="waiting">🕒 Espera</option>
                            <option value="done">✅ Resuelto</option>
                          </select>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <EditableCell 
                          value={data.assigned} 
                          onChange={(v) => updateTask(id, 'assigned', v)}
                        />
                      </td>
                      <td className="py-3 px-3">
                        <EditableCell 
                          value={data.detail} 
                          onChange={(v) => updateTask(id, 'detail', v)}
                          className="font-semibold"
                        />
                        <EditableCell 
                          value={data.desc} 
                          onChange={(v) => updateTask(id, 'desc', v)}
                          className="text-white/60 text-xs"
                        />
                      </td>
                      <td className="py-3 px-3">
                        <EditableCell 
                          value={data.comments || ''} 
                          onChange={(v) => updateTask(id, 'comments', v)}
                          className="text-white/60 text-xs italic"
                        />
                      </td>
                       <td className="py-3 px-3 text-center">
                        <button 
                            onClick={() => deleteTask(id)}
                            className="text-white/20 hover:text-red-400 transition-colors"
                            title="Eliminar Ticket"
                        >
                            <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
